import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { EDITOR_OVERLAY_Z_CLASS } from './editor-overlay-layer';

export interface DeleteConfirmationModalProps {
  /**
   * Controla a visibilidade do modal
   */
  isOpen: boolean;

  /**
   * Callback executado ao fechar o modal
   */
  onClose: () => void;

  /**
   * Callback executado ao confirmar a exclusão
   */
  onConfirm: () => void;

  /**
   * Título do modal
   * @default "Confirmar Exclusão"
   */
  title?: string;

  /**
   * Mensagem de confirmação principal
   * @example "Tem certeza que deseja excluir o usuário João Silva?"
   */
  message: string;

  /**
   * Mensagem de alerta/aviso opcional
   * @example "Atenção: Excluir esta etapa também irá excluir todos os leads dentro dela."
   */
  warningMessage?: string;

  /**
   * Estado de loading durante a exclusão
   */
  isLoading?: boolean;

  /**
   * Texto do botão de confirmação
   * @default "Excluir"
   */
  confirmButtonText?: string;

  /**
   * Texto do botão de cancelamento
   * @default "Cancelar"
   */
  cancelButtonText?: string;

  /**
   * Estilo visual: 'danger' (vermelho, ações destrutivas como excluir) ou
   * 'default' (roxo, ações neutras/reversíveis como ativar/desativar)
   * @default "danger"
   */
  variant?: 'danger' | 'default';

  /**
   * Texto do botão de confirmação durante o loading
   * @default "Excluindo..."
   */
  loadingText?: string;

  /**
   * Camada de empilhamento. `'default'` = `z-[60]`, o nível de overlay do DS.
   * `'editor'` = `EDITOR_OVERLAY_Z_CLASS`, acima do painel de nó e do seletor
   * de nós do editor de workflows (`z-[201]`) — sem isso a caixa abre ATRÁS
   * deles e o editor parece travado.
   * @default "default"
   */
  layer?: 'default' | 'editor';
}

/**
 * Modal genérico de confirmação de exclusão
 *
 * Componente reutilizável para confirmação de exclusão de dados em todo o sistema.
 * Segue o padrão always-light estabelecido para modais/dropdowns.
 *
 * @example
 * ```tsx
 * const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
 * const [isDeleting, setIsDeleting] = useState(false);
 *
 * const handleDelete = async () => {
 *   setIsDeleting(true);
 *   try {
 *     await api.delete(`stages/${stageId}`);
 *     toast.success('Etapa excluída com sucesso');
 *     setIsDeleteModalOpen(false);
 *   } catch (error) {
 *     toast.error('Erro ao excluir etapa');
 *   } finally {
 *     setIsDeleting(false);
 *   }
 * };
 *
 * <DeleteConfirmationModal
 *   isOpen={isDeleteModalOpen}
 *   onClose={() => setIsDeleteModalOpen(false)}
 *   onConfirm={handleDelete}
 *   message="Tem certeza que deseja excluir a etapa 'Qualificação'?"
 *   warningMessage="Atenção: Excluir esta etapa também irá excluir todos os leads dentro dela."
 *   isLoading={isDeleting}
 * />
 * ```
 */
export const DeleteConfirmationModal = React.forwardRef<HTMLDivElement, DeleteConfirmationModalProps>(
  ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirmar Exclusão',
    message,
    warningMessage,
    isLoading = false,
    confirmButtonText = 'Excluir',
    cancelButtonText = 'Cancelar',
    variant = 'danger',
    loadingText = 'Excluindo...',
    layer = 'default',
  }, ref) => {
    // Escape próprio, em CAPTURA. O painel de nó do editor de workflows escuta
    // Escape em `document` na fase de borbulha: sem este listener, o Esc fecha
    // o painel DE BAIXO e deixa esta caixa pendurada, com a Promise do
    // `useConfirm()` sem resolver. Captura roda antes da borbulha no mesmo
    // `document`, e o `stopPropagation` corta a borbulha inteira.
    // O efeito é declarado ANTES do `return` antecipado (regra dos hooks) e sai
    // cedo por dentro; a remoção repete o terceiro argumento `true`, senão o
    // listener de captura nunca sai (e o StrictMode monta tudo duas vezes).
    //
    // Só age na camada do editor, e isso é de propósito: até aqui esta caixa
    // NUNCA tratou Escape, e são 73 usos no produto (29 diretos + 44 pelo
    // `useConfirm()`). Ligar o Esc para todos seria mudar o comportamento de
    // telas que esta tarefa não podia tocar — inclusive engolindo o Esc de quem
    // escuta embaixo. Quem quiser o Esc pede `layer="editor"`.
    //
    // E nunca com a exclusão em curso: nesse estado os dois botões do rodapé
    // ficam `disabled`, e fechar pelo teclado furaria essa guarda, sumindo com
    // a caixa no meio da requisição — quem clicou ficaria sem saber se a
    // exclusão aconteceu.
    useEffect(() => {
      if (!isOpen || layer !== 'editor') return;
      const handleEscape = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape' || isLoading) return;
        event.stopPropagation();
        event.preventDefault();
        onClose();
      };
      document.addEventListener('keydown', handleEscape, true);
      return () => document.removeEventListener('keydown', handleEscape, true);
    }, [isOpen, isLoading, layer, onClose]);

    if (!isOpen) return null;

    const iconName = variant === 'danger' ? 'alert-triangle' : 'alert-circle';
    const iconBgColor = variant === 'danger' ? '#fee2e2' : '#f3e8ff';
    const iconColor = variant === 'danger' ? '#dc2626' : '#8b5cf6';
    const confirmBgColor = variant === 'danger' ? '#ef4444' : '#8b5cf6';
    const confirmBgColorHover = variant === 'danger' ? '#dc2626' : '#7c3aed';

    // Portal pro body + z-[60] (nível de overlay): sem portal fica preso a ancestral
    // com transform/filter; z-50 ficava atrás de Drawer z-[60]. Ver DS §Overlays.
    return createPortal(
      <div
        className={`fixed inset-0 flex items-end sm:items-center justify-center ${
          layer === 'editor' ? EDITOR_OVERLAY_Z_CLASS : 'z-[60]'
        } sm:p-4`}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        data-testid="confirm-dialog"
      >
        <div
          ref={ref}
          className="shadow-xl w-full sm:max-w-md rounded-t-2xl sm:rounded-lg max-h-[92vh] overflow-y-auto"
          style={{ backgroundColor: '#ffffff' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Grab handle (mobile bottom-sheet) */}
          <div className="sm:hidden flex justify-center pt-2 pb-0.5">
            <span className="w-9 h-1 rounded-full" style={{ backgroundColor: '#e2e8f0' }} />
          </div>

          {/* Header */}
          <div
            className="flex items-center justify-between p-4 border-b"
            style={{ borderColor: '#e5e7eb' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: iconBgColor }}>
                <Icon name={iconName} className="w-5 h-5" style={{ color: iconColor }} />
              </div>
              <h2
                className="text-lg font-semibold"
                style={{ color: '#1e293b' }}
              >
                {title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded transition-colors"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              disabled={isLoading}
            >
              <Icon name="x" className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="mb-4" style={{ color: '#1e293b' }}>
              {message}
            </p>

            {warningMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <span className="font-semibold">Atenção:</span> {warningMessage}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex gap-3 p-4 border-t"
            style={{ borderColor: '#e5e7eb' }}
          >
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border disabled:opacity-50 transition-colors"
              style={{
                color: '#1e293b',
                backgroundColor: '#ffffff',
                borderColor: '#e5e7eb',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
              disabled={isLoading}
              data-testid="confirm-dialog-cancel"
            >
              {cancelButtonText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{
                backgroundColor: confirmBgColor,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = confirmBgColorHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = confirmBgColor;
                }
              }}
              disabled={isLoading}
              data-testid="confirm-dialog-confirm"
            >
              {isLoading ? loadingText : confirmButtonText}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
);

DeleteConfirmationModal.displayName = 'DeleteConfirmationModal';
